import { useEffect, useState } from "react";
import { getUserData, authenticatedFetch } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  CreditCard, 
  Download, 
  Eye,
  Calendar,
  DollarSign,
  FileText,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Fatura {
  id: number;
  folio_id: string;
  invoice_number: string;
  description: string;
  total: number;
  currency: string;
  issue_date: string;
  due_date: string;
  paid_date: string | null;
  status: string;
  payment_method: string | null;
  invoice_file_url: string;
  notes: string | null;
}

const statusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: "Rascunho", color: "bg-gray-500/20 text-gray-700 dark:text-gray-400" },
  sent: { label: "Enviada", color: "bg-blue-500/20 text-blue-700 dark:text-blue-400" },
  overdue: { label: "Vencida", color: "bg-orange-500/20 text-orange-700 dark:text-orange-400" },
  paid: { label: "Paga", color: "bg-green-500/20 text-green-700 dark:text-green-400" },
  cancelled: { label: "Cancelada", color: "bg-red-500/20 text-red-700 dark:text-red-400" },
};

const ClientFaturas = () => {
  const [faturas, setFaturas] = useState<Fatura[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const user = getUserData();

  useEffect(() => {
    loadFaturas();
  }, []);

  const loadFaturas = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const response = await authenticatedFetch(`${API_BASE_URL}/faturas`);
      
      if (!response.ok) {
        throw new Error('Erro ao carregar faturas');
      }
      
      const data = await response.json();
      setFaturas(data.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar faturas:', err);
      setError(err.message || 'Erro ao carregar faturas');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleDownload = async (fatura: Fatura) => {
    if (!fatura.invoice_file_url) return;
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const filePath = fatura.invoice_file_url.replace('/uploads/', '');
      const fileUrl = `${API_BASE_URL}/uploads/serve.php?file=${encodeURIComponent(filePath)}`;
      
      // Fazer requisição autenticada
      const response = await authenticatedFetch(fileUrl);
      
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }
      
      // Converter resposta em blob
      const blob = await response.blob();
      
      // Criar URL temporária e fazer download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fatura_${fatura.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      alert('Erro ao baixar arquivo. Tente novamente.');
    }
  };

  const handleView = async (fatura: Fatura) => {
    if (!fatura.invoice_file_url) return;
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const filePath = fatura.invoice_file_url.replace('/uploads/', '');
      const fileUrl = `${API_BASE_URL}/uploads/serve.php?file=${encodeURIComponent(filePath)}`;
      
      // Fazer requisição autenticada
      const response = await authenticatedFetch(fileUrl);
      
      if (!response.ok) {
        throw new Error('Erro ao carregar arquivo');
      }
      
      // Converter resposta em blob
      const blob = await response.blob();
      
      // Criar URL temporária e abrir em nova aba
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Limpar URL após um tempo
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Erro ao visualizar arquivo:', error);
      alert('Erro ao visualizar arquivo. Tente novamente.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Carregando faturas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                <FileText className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Erro ao carregar faturas</h3>
                <p className="text-sm text-muted-foreground mt-2">{error}</p>
              </div>
              <Button onClick={loadFaturas} variant="outline">
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Faturas</h1>
              <p className="text-muted-foreground">Consulte suas faturas e histórico de pagamentos</p>
            </div>
          </div>
        </div>

        {/* Lista de Faturas */}
        {faturas.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4 py-8">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto">
                  <CreditCard className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Nenhuma fatura encontrada</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Você ainda não possui faturas cadastradas.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {faturas.map((fatura) => {
              const statusInfo = statusConfig[fatura.status] || statusConfig.draft;
              
              return (
                <Card key={fatura.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      {/* Info Principal */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold">
                                Fatura #{fatura.invoice_number}
                              </h3>
                              <Badge className={cn("text-xs", statusInfo.color)}>
                                {statusInfo.label}
                              </Badge>
                            </div>
                            {fatura.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {fatura.description}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">Emissão</p>
                              <p className="font-medium">{formatDate(fatura.issue_date)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">Vencimento</p>
                              <p className="font-medium">{formatDate(fatura.due_date)}</p>
                            </div>
                          </div>

                          {fatura.paid_date && (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-green-500" />
                              <div>
                                <p className="text-xs text-muted-foreground">Pagamento</p>
                                <p className="font-medium text-green-600">{formatDate(fatura.paid_date)}</p>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">Valor</p>
                              <p className="font-bold text-lg">{formatCurrency(fatura.total, fatura.currency)}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Ações */}
                      <div className="flex gap-2">
                        {fatura.invoice_file_url && (
                          <>
                            <Button
                              onClick={() => handleView(fatura)}
                              variant="outline"
                              size="sm"
                              className="gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              Visualizar
                            </Button>
                            <Button
                              onClick={() => handleDownload(fatura)}
                              variant="outline"
                              size="sm"
                              className="gap-2"
                            >
                              <Download className="h-4 w-4" />
                              Download
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientFaturas;
